import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { OutputSettingsType } from '@models/output-settings';
import { TranslateService } from '@ngx-translate/core';
import { SensorTagSlot } from 'icreate/common/models/sensor-cable-tag';
import { emptySlot, emptyValue } from 'icreate/common/models/slot';
import { Subject, fromEvent, takeUntil } from 'rxjs';

@Component({
    selector: 'app-slot-selector',
    templateUrl: './slot-selector.component.html',
    styleUrl: './slot-selector.component.css',
    standalone: false,
})
export class SlotSelectorComponent implements OnInit, OnChanges, OnDestroy {
    readonly fcuSelector = 'fcu';
    readonly fuSelector = 'fu';
    readonly slotSelector = 'slot';
    readonly NO_SELECTION = 'no-slection';

    @Input() vesselSlots: SensorTagSlot[];
    @Input() settingsType: OutputSettingsType;
    @Input() selectedSlot: SensorTagSlot;
    @Input() disabled: boolean = false;
    @Output() onSlotSelected: EventEmitter<SensorTagSlot> = new EventEmitter();
    @ViewChild('fcuSelElement') fcuSelElement: ElementRef;
    @ViewChild('fuSelElement') fuSelElement: ElementRef;
    @ViewChild('slotSelElement') slotSelElement: ElementRef;
    @ViewChild('selectorElement') selectorElement: ElementRef;

    filteredSlots: SensorTagSlot[];

    isFcuSelectorOpen: boolean = false;
    selectedFcuNum: number;
    isFuSelectorOpen: boolean = false;
    selectedFuNum: number | string;
    isSlotSelectorOpen: boolean = false;
    selectedSlotNum: number;

    selectorTop: string;

    optionsTop: string;
    optionsLeft: string;

    private destroy$: Subject<void> = new Subject();

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        const clickedOutsideSelector = !this.selectorElement?.nativeElement.contains(event.target);
        if (clickedOutsideSelector) {
            this.isFcuSelectorOpen = false;
            this.isFuSelectorOpen = false;
            this.isSlotSelectorOpen = false;
        }
    }

    constructor(
        private cdr: ChangeDetectorRef,
        private translate: TranslateService
    ) {}

    ngOnInit(): void {
        if (this.selectedSlot) {
            this.selectedFcuNum = this.selectedSlot.fcuNum;
            this.selectedFuNum = this.selectedSlot.fuNum ? this.selectedSlot.fuNum : this.NO_SELECTION;
            this.selectedSlotNum = this.selectedSlot.no;
        }
        const scrollContainer = document.querySelector('.system-setting-scroll-parent');
        if (scrollContainer) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.isFcuSelectorOpen = false;
                    this.isFuSelectorOpen = false;
                    this.isSlotSelectorOpen = false;
                });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.settingsType) {
            this.filteredSlots = this.vesselSlots.filter(slot => {
                if (this.settingsType == OutputSettingsType.AO) return slot.ioType == 'AO (4-20mA)';
                else if (this.settingsType == OutputSettingsType.DO) return slot.ioType == 'DO';
            });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    openFcuSelector(event) {
        if (this.disabled) return;
        this.isFuSelectorOpen = false;
        this.isSlotSelectorOpen = false;
        if (this.isFcuSelectorOpen) {
            this.updatePosition();
            this.isFcuSelectorOpen = false;
        } else {
            this.updatePosition(this.fcuSelElement);
            this.isFcuSelectorOpen = true;
        }
        event.stopPropagation();
    }

    openFuSelector(event) {
        if (this.disabled || !this.selectedFcuNum) return;
        this.isFcuSelectorOpen = false;
        this.isSlotSelectorOpen = false;
        if (this.isFuSelectorOpen) {
            this.updatePosition();
            this.isFuSelectorOpen = false;
        } else {
            this.updatePosition(this.fuSelElement);
            this.isFuSelectorOpen = true;
        }
        event.stopPropagation();
    }

    openSlotSelector(event) {
        if (this.disabled || !this.selectedFcuNum || (!this.selectedFuNum && this.selectedFuNum != this.NO_SELECTION))
            return;
        this.isFcuSelectorOpen = false;
        this.isFuSelectorOpen = false;
        if (this.isSlotSelectorOpen) {
            this.updatePosition();
            this.isSlotSelectorOpen = false;
        } else {
            this.updatePosition(this.slotSelElement);
            this.isSlotSelectorOpen = true;
        }
        event.stopPropagation();
    }

    getSelectorOptions(selector) {
        if (!this.vesselSlots?.length) return [];

        let key;
        if (selector == this.fcuSelector) key = 'fcuNum';
        if (selector == this.fuSelector) key = 'fuNum';
        if (selector == this.slotSelector) key = 'no';

        if (!key) return [];

        const filters = {};

        if (this.selectedFcuNum) filters['fcuNum'] = this.selectedFcuNum;
        if (this.selectedFuNum && key !== 'fcuNum') {
            if (this.selectedFuNum == this.NO_SELECTION) filters['fuNum'] = undefined;
            else filters['fuNum'] = this.selectedFuNum;
        }

        if (selector === this.fcuSelector) delete filters['fcuNum'];
        if (selector === this.fuSelector) delete filters['fuNum'];

        const options = this.filteredSlots
            .filter(slot => {
                if (slot.ioType === emptySlot || slot.ioType == emptyValue) return false;
                return Object.entries(filters).every(([k, val]) => slot[k] === val);
            })
            .map(slot => slot[key])
            .filter(Boolean)
            .sort((a, b) => a - b);

        if (selector == this.fuSelector) {
            options.unshift(this.NO_SELECTION);
        }
        return [...new Set(options)];
    }

    async optionSelected(selector, option) {
        if (this.disabled) return;
        if (selector == this.fcuSelector) {
            this.selectedFcuNum = option;
            this.selectedFuNum = null;
            this.selectedSlotNum = null;
        } else if (selector == this.fuSelector) {
            this.selectedFuNum = option;
            this.selectedSlotNum = null;
        } else if (selector == this.slotSelector) {
            this.selectedSlotNum = option;
        }

        this.updateSelectedSlot();
        this.cdr.detectChanges();
    }

    formatFcu(option: number): string {
        return this.translate.instant('sensor-cable-tag.selected.fcu', { num: option });
    }

    formatFu(option): string {
        return option == this.NO_SELECTION
            ? this.getNoSelectionText()
            : this.translate.instant('sensor-cable-tag.selected.fu', { num: option });
    }

    getNoSelectionText() {
        return this.translate.instant('sensor-cable-tag.filter.no-selection');
    }

    paddNumber(number: number) {
        return number.toString().padStart(2, '0');
    }

    getDisplayValue(selector) {
        if (selector == this.fcuSelector) {
            if (!this.selectedFcuNum) {
                return this.translate.instant('sensor-cable-tag.filter.select');
            } else {
                return this.translate.instant('sensor-cable-tag.selected.fcu', {
                    num: this.selectedFcuNum,
                });
            }
        } else if (selector == this.fuSelector) {
            if (!this.selectedFuNum) {
                return this.translate.instant('sensor-cable-tag.filter.select');
            } else if (this.selectedFuNum == this.NO_SELECTION) {
                return this.getNoSelectionText();
            } else {
                return this.translate.instant('sensor-cable-tag.selected.fu', {
                    num: this.selectedFuNum,
                });
            }
        } else if (selector == this.slotSelector) {
            if (!this.selectedSlotNum) {
                return this.translate.instant('sensor-cable-tag.filter.select');
            } else {
                return this.paddNumber(this.selectedSlotNum);
            }
        }
    }

    updateSelectedSlot() {
        let fuNum = this.selectedFuNum;
        if (this.selectedFuNum == this.NO_SELECTION) fuNum = undefined;
        const slot = this.filteredSlots.find(
            slot => this.selectedFcuNum === slot.fcuNum && fuNum === slot.fuNum && this.selectedSlotNum === slot.no
        );

        this.onSlotSelected.emit(slot);
    }

    private updatePosition(elementRef?) {
        if (!elementRef) {
            this.optionsTop = null;
            this.optionsLeft = null;
            return;
        }
        const selectorPosition = elementRef.nativeElement.getBoundingClientRect();

        const scrollTop = window.scrollY;
        const scrollLeft = window.scrollX;

        let top = selectorPosition.bottom + scrollTop;

        this.optionsTop = top + 1 + 'px';
        this.optionsLeft = selectorPosition.left + scrollLeft + 'px';

        this.cdr.detectChanges();
    }

    unselectSlot() {
        this.selectedFcuNum = undefined;
        this.selectedFuNum = undefined;
        this.selectedSlotNum = undefined;
    }
}
